/**
 * Ask CIGS — a slide-over panel that runs a question through the read-only agent loop at
 * POST /api/ask and streams the answer back. Same Sheet pattern as the registry's
 * CapabilityDrawer (src/features/registry/CapabilityDrawer.tsx): the parent owns `open` and gets
 * told when to close, this component owns everything that happens inside.
 *
 * Three things stream in over one SSE connection (see ./api.ts#streamAsk):
 *   - `tool` events narrate what the loop is doing right now ("Searching the library for…")
 *   - `delta` events are the answer, appended as they arrive
 *   - `done` carries the citations the answer actually used, once, at the end
 */
import { useRef, useState } from 'react';
import { KeyRound, Loader2, PowerOff, Send, Sparkles, Square } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { EmptyState } from '@/components/EmptyState';
import { cn } from '@/lib/utils';
import { streamAsk, useAskStatus } from './api';
import { Answer } from './Answer';
import type { Citation } from './types';

const MIN_QUESTION_LENGTH = 3;

export interface AskPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * A question to start from, set fresh each time the panel opens. It exists so a
   * launcher can carry its context in — asking from the IT health card should not
   * make you type the word "IT" again. Still fully editable before sending.
   */
  initialQuestion?: string;
}

export function AskPanel({ open, onOpenChange, initialQuestion = '' }: AskPanelProps) {
  const status = useAskStatus();
  const [question, setQuestion] = useState(initialQuestion);
  // Re-seed on each open rather than in an effect on `initialQuestion`: the
  // launcher's context can change while the panel is shut, and a half-typed
  // question should never be overwritten while it is open.
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (open && seededFor !== initialQuestion) {
    setSeededFor(initialQuestion);
    setQuestion(initialQuestion);
  }
  if (!open && seededFor !== null) setSeededFor(null);
  const [streaming, setStreaming] = useState(false);
  const [toolDetail, setToolDetail] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');
  const [citations, setCitations] = useState<Citation[]>([]);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const ready = status.data?.state === 'ready';
  const canSubmit = ready && !streaming && question.trim().length >= MIN_QUESTION_LENGTH;

  const submit = async () => {
    if (!canSubmit) return;
    const asked = question.trim();
    setStreaming(true);
    setToolDetail(null);
    setAnswer('');
    setCitations([]);
    setError(null);

    const controller = new AbortController();
    controllerRef.current = controller;
    // A stream that ends having said neither `done` nor `error` did not finish
    // — the connection dropped, a proxy cut it, or the server died mid-answer.
    // Without this the panel just stops, which is indistinguishable from an
    // answer that happened to be short, and is how a real outage went unnoticed.
    let settled = false;
    let sawAnything = false;
    try {
      await streamAsk({
        question: asked,
        signal: controller.signal,
        onEvent: (event) => {
          sawAnything = true;
          if (event.type === 'tool') setToolDetail(event.detail);
          else if (event.type === 'delta') setAnswer((prev) => prev + event.text);
          else if (event.type === 'done') {
            settled = true;
            setCitations(event.citations);
            setToolDetail(null);
          } else if (event.type === 'error') {
            settled = true;
            setError(event.message);
            setToolDetail(null);
          }
        },
      });
      if (!settled && !controller.signal.aborted) {
        setToolDetail(null);
        setError(
          sawAnything
            ? 'The connection dropped before Ask CIGS finished. The answer above may be incomplete — ask again to retry.'
            : 'Ask CIGS closed the connection without answering. Try again, or check the API logs if it keeps happening.',
        );
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setError(err instanceof Error ? err.message : 'Ask CIGS could not answer that.');
      }
      setToolDetail(null);
    } finally {
      setStreaming(false);
      controllerRef.current = null;
    }
  };

  const stop = () => controllerRef.current?.abort();

  const handleOpenChange = (next: boolean) => {
    if (!next) stop();
    onOpenChange(next);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <SheetTitle className="flex-1 text-xl leading-tight">Ask CIGS</SheetTitle>
            {ready && status.data ? (
              <Badge variant="secondary" className="gap-1">
                <Sparkles className="h-3 w-3" aria-hidden />
                {status.data.model}
              </Badge>
            ) : null}
          </div>
          <SheetDescription>
            Ask a question about the capability registry and the document library. Every claim in
            the answer links back to the passage or record it came from.
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-5 py-4">
          {status.isLoading ? (
            <div className="animate-pulse rounded-md border border-border bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
              Checking whether Ask CIGS is available…
            </div>
          ) : !ready ? (
            <NotReadyBody hint={status.data?.hint ?? status.error?.message ?? 'Ask CIGS is unavailable right now.'} off={status.data?.state === 'off'} />
          ) : null}

          <AskComposer
            question={question}
            onQuestionChange={setQuestion}
            onSubmit={submit}
            onStop={stop}
            disabled={!ready}
            streaming={streaming}
            canSubmit={canSubmit}
          />

          {toolDetail ? (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />
              <span className="truncate">{toolDetail}</span>
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          {answer || (streaming && !toolDetail) ? (
            <div className="rounded-md border border-border bg-card px-4 py-3">
              <Answer text={answer} citations={citations} />
              {streaming && !toolDetail ? (
                <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-foreground/60 align-middle" aria-hidden />
              ) : null}
            </div>
          ) : null}

          {citations.length > 0 ? <Sources citations={citations} /> : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function AskComposer({
  question,
  onQuestionChange,
  onSubmit,
  onStop,
  disabled,
  streaming,
  canSubmit,
}: {
  question: string;
  onQuestionChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  disabled: boolean;
  streaming: boolean;
  canSubmit: boolean;
}) {
  return (
    <div className="space-y-2">
      <Textarea
        value={question}
        onChange={(e) => onQuestionChange(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder="Which IT capabilities are on the hot list, and what does the SRD say about them?"
        rows={3}
        disabled={disabled}
        aria-label="Question for Ask CIGS"
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">⌘/Ctrl + Enter to ask</span>
        {streaming ? (
          <Button type="button" size="sm" variant="outline" onClick={onStop} className="gap-1.5">
            <Square className="h-3.5 w-3.5" aria-hidden />
            Stop
          </Button>
        ) : (
          <Button type="button" size="sm" onClick={onSubmit} disabled={!canSubmit} className="gap-1.5">
            <Send className="h-3.5 w-3.5" aria-hidden />
            Ask
          </Button>
        )}
      </div>
    </div>
  );
}

function NotReadyBody({ hint, off }: { hint: string; off: boolean }) {
  return (
    <EmptyState
      icon={off ? <PowerOff className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
      title={off ? 'Ask CIGS is switched off' : 'Ask CIGS needs a provider key'}
      description={hint}
      className="py-10"
    />
  );
}

function Sources({ citations }: { citations: Citation[] }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sources</h3>
      <ul className="space-y-1.5">
        {citations.map((citation) => (
          <li key={citation.handle}>
            <Link
              to={citation.href}
              className={cn(
                'flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm transition-colors duration-150 hover:bg-muted',
              )}
            >
              <span className="id-mono shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                {citation.handle}
              </span>
              {citation.kind === 'section' ? (
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{citation.documentTitle}</span>
                  <span className="id-mono block truncate text-xs text-muted-foreground">{citation.headingPath}</span>
                </span>
              ) : (
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{citation.name}</span>
                  <span className="id-mono block truncate text-xs text-muted-foreground">{citation.entityKey}</span>
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
